import { 
  doc, 
  getDoc,
  collection,
  query,
  orderBy,
  getDocs,
  limit,
} from 'firebase/firestore';
import { db } from '../../firebase';

// Points Service for Firestore operations
export const pointsService = {
  /**
   * Get user's total points and breakdown
   * @param {string} userId - User's Firebase UID
   * @returns {Promise<Object>} Points data
   */
  getUserPoints: async (userId) => {
    try {
      const pointsRef = doc(db, 'userPoints', userId);
      const pointsDoc = await getDoc(pointsRef);

      if (!pointsDoc.exists()) {
        console.log('ℹ️ No points found for user:', userId);
        return {
          totalPoints: 0,
          pointsByRace: {},
          lastUpdated: null,
        };
      }

      const pointsData = pointsDoc.data();
      console.log('✅ User points retrieved:', pointsData.totalPoints);
      
      return {
        totalPoints: pointsData.totalPoints || 0,
        pointsByRace: pointsData.pointsByRace || {},
        lastUpdated: pointsData.lastUpdated,
      };
    } catch (error) {
      console.error('❌ Error getting user points:', error);
      throw error;
    }
  },

  /**
   * Get points for a specific race
   * @param {string} userId - User's Firebase UID
   * @param {string} raceId - Race identifier
   * @returns {Promise<number>} Points earned for race
   */
  getPointsByRace: async (userId, raceId) => {
    try {
      const pointsData = await pointsService.getUserPoints(userId);
      
      const racePoints = pointsData.pointsByRace[raceId] || 0;
      console.log(`✅ Points for race ${raceId}:`, racePoints);
      
      return racePoints;
    } catch (error) {
      console.error('❌ Error getting points by race:', error);
      throw error;
    }
  },

  /**
   * Get global leaderboard (top users by points)
   * @param {number} limitCount - Number of users to return (default: 100)
   * @returns {Promise<Array>} Array of users with points
   */
  getGlobalLeaderboard: async (limitCount = 100) => {
    try {
      const leaderboardQuery = query(
        collection(db, 'userPoints'),
        orderBy('totalPoints', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(leaderboardQuery);
      const leaderboard = [];

      // Fetch user data for each entry
      for (const pointsDoc of querySnapshot.docs) {
        const userId = pointsDoc.id;
        const pointsData = pointsDoc.data();

        // Get user display info
        const userRef = doc(db, 'users', userId);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.exists() ? userDoc.data() : {};

        leaderboard.push({
          userId,
          displayName: userData.displayName || 'Unknown User',
          email: userData.email || '',
          photoURL: userData.photoURL || '',
          totalPoints: pointsData.totalPoints || 0,
          position: leaderboard.length + 1,
        });
      }

      console.log('✅ Global leaderboard retrieved:', leaderboard.length);
      return leaderboard;
    } catch (error) {
      console.error('❌ Error getting global leaderboard:', error);
      throw error;
    }
  },

  /**
   * Get user's rank in global leaderboard
   * @param {string} userId - User's Firebase UID
   * @returns {Promise<Object>} Rank information
   */
  getUserRank: async (userId) => {
    try {
      const userPoints = await pointsService.getUserPoints(userId);
      
      // Count users with more points
      const higherPointsQuery = query(
        collection(db, 'userPoints'),
        orderBy('totalPoints', 'desc')
      );

      const querySnapshot = await getDocs(higherPointsQuery);
      let rank = 1;

      for (const doc of querySnapshot.docs) {
        if (doc.id === userId) {
          break;
        }
        rank++;
      }

      console.log('✅ User rank calculated:', rank);
      
      return {
        rank,
        totalPoints: userPoints.totalPoints,
        totalUsers: querySnapshot.size,
      };
    } catch (error) {
      console.error('❌ Error getting user rank:', error);
      throw error;
    }
  },
};

export default pointsService;

